import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    CompanySummary,
    PageResponse,
    fetchCompanies,
} from "../services/polarisService";
import PolarisRowActionsMenu, { type PolarisRowAction } from "./PolarisRowActionsMenu";
import "../styles/polaris-admin.css";

const PAGE_SIZE = 20;

function formatDate(iso: string | null | undefined): string {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function statusBadgeClass(status: string): string {
    const s = status.toLowerCase();
    if (s === "active") return "pa-badge pa-badge-active";
    if (s === "trialing") return "pa-badge pa-badge-trialing";
    if (s === "canceled" || s === "cancelled") return "pa-badge pa-badge-canceled";
    if (s === "past_due") return "pa-badge pa-badge-past_due";
    if (s === "none") return "pa-badge pa-badge-none";
    return "pa-badge pa-badge-inactive";
}

function getPageNumbers(current: number, total: number): (number | "ellipsis")[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);
    const pages: (number | "ellipsis")[] = [];
    pages.push(0);
    if (current > 3) pages.push("ellipsis");
    for (let i = Math.max(1, current - 2); i <= Math.min(total - 2, current + 2); i++) {
        pages.push(i);
    }
    if (current < total - 4) pages.push("ellipsis");
    pages.push(total - 1);
    return pages;
}

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalElements: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    itemLabel?: string;
}

function Pagination({ currentPage, totalPages, totalElements, pageSize, onPageChange, itemLabel = "items" }: PaginationProps) {
    if (totalPages <= 1) return null;
    const start = currentPage * pageSize + 1;
    const end = Math.min(currentPage * pageSize + pageSize, totalElements);
    const pages = getPageNumbers(currentPage, totalPages);

    return (
        <div className="pa-pagination">
            <div className="pa-pagination-info">
                Showing <strong>{start}–{end}</strong> of <strong>{totalElements}</strong> {itemLabel}
            </div>
            <div className="pa-pagination-controls">
                <button className="pa-page-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 0} aria-label="Previous page">Previous</button>
                {pages.map((p, i) =>
                    p === "ellipsis" ? (
                        <span key={`e-${i}`} className="pa-page-ellipsis">…</span>
                    ) : (
                        <button key={p} className={`pa-page-btn${p === currentPage ? " active" : ""}`} onClick={() => onPageChange(p as number)} aria-label={`Page ${(p as number) + 1}`} aria-current={p === currentPage ? "page" : undefined}>
                            {(p as number) + 1}
                        </button>
                    )
                )}
                <button className="pa-page-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} aria-label="Next page">Next</button>
            </div>
        </div>
    );
}

type CompanySortKey = keyof CompanySummary;

interface CompanyFilters {
    companyName: string;
    districtName: string;
    schoolName: string;
    type: string;
    planName: string;
    subscriptionStatus: string;
}

const EMPTY_COMPANY_FILTERS: CompanyFilters = {
    companyName: "",
    districtName: "",
    schoolName: "",
    type: "",
    planName: "",
    subscriptionStatus: "",
};

export default function PolarisOverviewPage() {
    const navigate = useNavigate();
    const [data, setData] = useState<PageResponse<CompanySummary> | null>(null);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [paging, setPaging] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<CompanySortKey>("subscriptionDate");
    const [sortAsc, setSortAsc] = useState(false);
    const [filters, setFilters] = useState<CompanyFilters>(EMPTY_COMPANY_FILTERS);
    const [debouncedFilters, setDebouncedFilters] = useState<CompanyFilters>(EMPTY_COMPANY_FILTERS);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            setDebouncedFilters({
                companyName: filters.companyName.trim(),
                districtName: filters.districtName.trim(),
                schoolName: filters.schoolName.trim(),
                type: filters.type.trim(),
                planName: filters.planName.trim(),
                subscriptionStatus: filters.subscriptionStatus.trim(),
            });
        }, 300);
        return () => window.clearTimeout(handle);
    }, [filters]);

    const load = useCallback(async (p: number, companyFilters: CompanyFilters, isInitial: boolean) => {
        if (isInitial) setLoading(true); else setPaging(true);
        setError(null);
        try {
            const result = await fetchCompanies(p, PAGE_SIZE, {
                ...(companyFilters.companyName ? { companyName: companyFilters.companyName } : {}),
                ...(companyFilters.districtName ? { districtName: companyFilters.districtName } : {}),
                ...(companyFilters.schoolName ? { schoolName: companyFilters.schoolName } : {}),
                ...(companyFilters.type ? { type: companyFilters.type } : {}),
                ...(companyFilters.planName ? { planName: companyFilters.planName } : {}),
                ...(companyFilters.subscriptionStatus
                    ? { subscriptionStatus: companyFilters.subscriptionStatus }
                    : {}),
            });
            setData(result);
        } catch (e: any) {
            setError(e.message ?? "Unable to load companies. Please try again.");
        } finally {
            setLoading(false);
            setPaging(false);
        }
    }, []);

    const isInitialLoad = useRef(true);
    const prevFiltersRef = useRef(debouncedFilters);
    useEffect(() => {
        const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(debouncedFilters);
        prevFiltersRef.current = debouncedFilters;
        if (filtersChanged && page !== 0) {
            setPage(0);
            return;
        }
        const initial = isInitialLoad.current;
        isInitialLoad.current = false;
        load(page, debouncedFilters, initial);
    }, [page, debouncedFilters, load]);

    const handleSort = (key: CompanySortKey) => {
        if (sortKey === key) setSortAsc((a) => !a);
        else { setSortKey(key); setSortAsc(true); }
    };

    const sorted = useMemo(() => {
        if (!data) return [];
        return [...data.content].sort((a, b) => {
            const av = a[sortKey] ?? "";
            const bv = b[sortKey] ?? "";
            const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
            return sortAsc ? cmp : -cmp;
        });
    }, [data, sortKey, sortAsc]);

    const SortTh = ({ col, label }: { col: CompanySortKey; label: string }) => (
        <th className="sortable" onClick={() => handleSort(col)}>
            {label}<span className="pa-sort-indicator">{sortKey === col ? (sortAsc ? " ▲" : " ▼") : " ⇅"}</span>
        </th>
    );

    return (
        <section className="pa-section">
            <h2 className="pa-section-title">Companies &amp; Schools</h2>
            {error && <div className="pa-error">{error}</div>}
            {loading && <div className="pa-loading">Loading companies…</div>}
            {!loading && !error && (
                <div className="pa-table-wrapper" style={{ opacity: paging ? 0.5 : 1, transition: "opacity 0.15s ease" }}>
                    <table className="pa-table">
                        <thead>
                            <tr>
                                <SortTh col="companyName" label="Company Name" />
                                <SortTh col="districtName" label="District Name" />
                                <SortTh col="schoolName" label="School Name" />
                                <SortTh col="type" label="Type" />
                                <SortTh col="subscriptionDate" label="Subscribed" />
                                <SortTh col="subscriptionEnd" label="Ends" />
                                <SortTh col="adminCount" label="Admins" />
                                <SortTh col="staffCount" label="Staff" />
                                <SortTh col="coachCount" label="Coaches" />
                                <SortTh col="athleteCount" label="Athletes" />
                                <SortTh col="planName" label="Plan" />
                                <SortTh col="billingInterval" label="Billing" />
                                <SortTh col="subscriptionStatus" label="Status" />
                                <th>Actions</th>
                            </tr>
                            <tr className="pa-filter-row">
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.companyName} onChange={(e) => setFilters((f) => ({ ...f, companyName: e.target.value }))} /></th>
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.districtName} onChange={(e) => setFilters((f) => ({ ...f, districtName: e.target.value }))} /></th>
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.schoolName} onChange={(e) => setFilters((f) => ({ ...f, schoolName: e.target.value }))} /></th>
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))} /></th>
                                <th /><th /><th /><th />
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.planName} onChange={(e) => setFilters((f) => ({ ...f, planName: e.target.value }))} /></th>
                                <th />
                                <th><input className="pa-filter-input" placeholder="Filter…" value={filters.subscriptionStatus} onChange={(e) => setFilters((f) => ({ ...f, subscriptionStatus: e.target.value }))} /></th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.length === 0 ? (
                                <tr><td colSpan={14} style={{ textAlign: "center", padding: "24px", color: "var(--muted)" }}>No companies found</td></tr>
                            ) : (
                                sorted.map((c) => (
                                    <tr key={c.companyId}>
                                        <td>{c.companyName ?? "—"}</td>
                                        <td>{c.districtName ?? "—"}</td>
                                        <td>{c.schoolName ?? "—"}</td>
                                        <td>{c.type ?? "—"}</td>
                                        <td>{formatDate(c.subscriptionDate)}</td>
                                        <td>{formatDate(c.subscriptionEnd)}</td>
                                        <td>{c.adminCount}</td>
                                        <td>{c.staffCount}</td>
                                        <td>{c.coachCount}</td>
                                        <td>{c.athleteCount}</td>
                                        <td>{c.planName ?? "—"}</td>
                                        <td>{c.billingInterval ?? "—"}</td>
                                        <td><span className={statusBadgeClass(c.subscriptionStatus)}>{c.subscriptionStatus}</span></td>
                                        <td>
                                            <PolarisRowActionsMenu
                                                actions={[
                                                    {
                                                        id: "view-ai-usage",
                                                        label: "View AI usage",
                                                        description: "Usage and company token limits",
                                                        onSelect: () =>
                                                            navigate(`/polaris-admin/companies/${c.companyId}/ai-usage`),
                                                        title: `View AI usage for ${c.companyName ?? "company"}`,
                                                        testId: `view-company-ai-usage-${c.companyId}`,
                                                    } satisfies PolarisRowAction,
                                                    {
                                                        id: "instant-chat",
                                                        label: "Instant Chat",
                                                        description: "Turn Instant Chat on or off for this organisation",
                                                        onSelect: () =>
                                                            navigate(
                                                                `/polaris-admin/instant-chat?tab=companies&companyName=${encodeURIComponent(c.companyName ?? "")}`,
                                                            ),
                                                        title: `Instant Chat for ${c.companyName ?? "company"}`,
                                                        testId: `view-company-instant-chat-${c.companyId}`,
                                                    } satisfies PolarisRowAction,
                                                ]}
                                                ariaLabel={`Actions for ${c.companyName ?? "company"}`}
                                                data-testid={`company-actions-${c.companyId}`}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    {data && (
                        <Pagination
                            currentPage={page}
                            totalPages={data.totalPages}
                            totalElements={data.totalElements}
                            pageSize={PAGE_SIZE}
                            onPageChange={setPage}
                            itemLabel="companies"
                        />
                    )}
                </div>
            )}
        </section>
    );
}
